import React from "react";
import { Check, ChevronDown } from "lucide-react";

export type PasoDeMarca = {
  clave: string;
  etiqueta: string;
  listo: boolean;
  /** Abre el lugar donde se completa (un tab de Mi marca). */
  ir: () => void;
};

/**
 * "Tu marca está al N%" en la cabecera de Mi marca —
 * ADR-2026-10-02-mi-marca-completitud.
 *
 * Un anillo con el porcentaje y, al tocarlo, la lista de lo que tiene y lo que le falta
 * a la marca, cada faltante con su atajo al tab donde se carga. Cuanto más sepa Atlas de
 * la marca, mejor genera; esto lo hace visible sin mandar a nadie a un formulario.
 *
 * Al 100% queda un "Marca completa" chico y quieto: no hay nada que ofrecer.
 */
export const CompletitudDeMarca = ({
  pasos,
  onAbiertoChange,
}: {
  pasos: PasoDeMarca[];
  /**
   * Avisa cuando se abre o se cierra la lista: quien la contiene tiene que subir de
   * capa mientras está abierta (ver `CabeceraDeMarca`).
   */
  onAbiertoChange?: (abierto: boolean) => void;
}) => {
  const [abierto, setAbierto] = React.useState(false);
  const raizRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    onAbiertoChange?.(abierto);
  }, [abierto, onAbiertoChange]);

  // Cerrar con click afuera o Escape.
  React.useEffect(() => {
    if (!abierto) return;
    const alClick = (e: MouseEvent) => {
      if (!raizRef.current?.contains(e.target as Node)) setAbierto(false);
    };
    const alTecla = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAbierto(false);
    };
    document.addEventListener("mousedown", alClick);
    document.addEventListener("keydown", alTecla);
    return () => {
      document.removeEventListener("mousedown", alClick);
      document.removeEventListener("keydown", alTecla);
    };
  }, [abierto]);

  const hechos = pasos.filter((p) => p.listo).length;
  const faltan = pasos.length - hechos;
  const porcentaje = pasos.length ? Math.round((hechos / pasos.length) * 100) : 0;

  if (faltan === 0) {
    return (
      <span className="inline-flex h-9 items-center gap-1.5 rounded-full bg-white/80 px-3 text-[13px] font-medium text-emerald-700 shadow-[0_1px_2px_rgba(16,24,40,0.06),0_0_0_1px_rgba(16,24,40,0.06)] backdrop-blur">
        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white">
          <Check className="h-2.5 w-2.5" strokeWidth={3} />
        </span>
        Marca completa
      </span>
    );
  }

  // Anillo: circunferencia de un círculo de radio 15.
  const C = 2 * Math.PI * 15;

  return (
    <div ref={raizRef} className="relative">
      {/* Anillo con el porcentaje adentro + "Completá tu marca" / "Te faltan N pasos".
          Hubo una versión compacta de una línea (ADR-2026-10-02-mi-marca-agregar-directo)
          y se volvió a ésta: decía mejor qué era —
          ADR-2026-10-02-mi-marca-pill-con-titulo. */}
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        // A todo el ancho abajo de `lg` (renglón propio en la cabecera mobile —
        // ADR-2026-10-05-mi-marca-cabecera-en-mobile); al lado del nombre, lo que mide.
        className="flex w-full lg:w-auto items-center gap-3 rounded-2xl bg-white/80 py-2 pl-2 pr-3 text-left shadow-[0_1px_2px_rgba(16,24,40,0.06),0_0_0_1px_rgba(16,24,40,0.06)] backdrop-blur transition-colors hover:bg-white"
      >
        <span className="relative flex h-10 w-10 shrink-0 items-center justify-center">
          <svg viewBox="0 0 36 36" className="absolute inset-0 -rotate-90" aria-hidden>
            <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(16,24,40,0.08)" strokeWidth="3.5" />
            <circle
              cx="18"
              cy="18"
              r="15"
              fill="none"
              stroke="var(--brand-primary)"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeDasharray={C}
              strokeDashoffset={C * (1 - porcentaje / 100)}
              className="transition-[stroke-dashoffset] duration-700 ease-out"
            />
          </svg>
          <span className="text-[10.5px] font-semibold tabular-nums text-[#16181D]">
            {porcentaje}%
          </span>
        </span>
        <span className="flex-1 lg:flex-none">
          <span className="block whitespace-nowrap text-[13.5px] font-semibold leading-tight text-[#16181D]">
            Completá tu marca
          </span>
          <span className="mt-0.5 block whitespace-nowrap text-[12px] leading-tight text-black/50">
            {faltan === 1 ? "Te falta 1 paso" : `Te faltan ${faltan} pasos`}
          </span>
        </span>
        <ChevronDown
          className={`h-4 w-4 text-black/35 transition-transform ${abierto ? "rotate-180" : ""}`}
        />
      </button>

      {abierto && (
        <div className="absolute left-0 top-full z-30 mt-2 w-full lg:w-[300px] rounded-2xl border border-black/[0.06] bg-white p-2 shadow-[0_16px_40px_-12px_rgba(16,24,40,0.25)] animate-fade-in">
          <div className="px-2.5 pb-2 pt-1.5">
            <p className="text-[13.5px] font-semibold text-[#16181D]">
              {hechos} de {pasos.length} listos
            </p>
            <p className="text-[12px] leading-snug text-black/50">
              Cuanto más sepa Atlas de tu marca, mejor sale lo que crea.
            </p>
          </div>
          <ul>
            {pasos.map((p) => (
              <li key={p.clave}>
                <button
                  type="button"
                  disabled={p.listo}
                  onClick={() => {
                    setAbierto(false);
                    p.ir();
                  }}
                  className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-[13.5px] transition-colors enabled:hover:bg-black/[0.03]"
                >
                  {p.listo ? (
                    <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                  ) : (
                    <span className="h-[18px] w-[18px] shrink-0 rounded-full border-[1.5px] border-dashed border-black/25" />
                  )}
                  <span className={`flex-1 ${p.listo ? "text-black/40 line-through decoration-black/20" : "font-medium text-[#16181D]"}`}>
                    {p.etiqueta}
                  </span>
                  {!p.listo && (
                    <span className="text-[12.5px] font-semibold text-brand-primary">Agregar</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
